import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "q-pa-md" }
const _hoisted_2 = { class: "row no-wrap justify-between q-col-gutter-x-md" }
const _hoisted_3 = { class: "row items-center" }
const _hoisted_4 = { class: "text-h4 text-weight-medium" }
const _hoisted_5 = {
  key: 0,
  class: "q-gutter-xs q-pl-sm"
}
const _hoisted_6 = { class: "text-center column" }
const _hoisted_7 = { class: "q-mt-sm q-gutter-sm" }
const _hoisted_8 = { class: "text-h6" }
const _hoisted_9 = ["innerHTML"]

import { computed, ComputedRef, inject, onBeforeUpdate, reactive, Ref, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import StoreBase from '../components/StoreBase.vue'
import FilteringDataDisplay from '../components/FilteringDataDisplay.vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useTimers } from '../store/timers'
import { useUserStore } from '../store/useUserStore'
import { useInventory } from '../store/useInventory'
import { useStoreProfiles } from '../store/useStoreProfiles'
import { DisplayType } from '../types/components/data-rendering'
import { Capacitor } from '@capacitor/core'
import { useFormatTime } from '../composables/formatTime'
import ModalStoreEndVisitWarning from '../components/store_profile/ModalStoreEndVisitWarning.vue'
import StorePicklist from '../components/store_profile/StorePicklist.vue'
import StoreTasks from '../components/store_profile/StoreTasks.vue'
import StoreRecaps from '../components/store_profile/StoreRecaps.vue'
import { parseBool } from '../utils'
import { checkMaintenance } from '../components/ModalMaintenance.vue'

export type StoreStates = {
  isThisTimer: ComputedRef<boolean>
  noActiveTimer: ComputedRef<boolean>
  thisStoreInTimers: ComputedRef<boolean>
  showResume: Ref<boolean>
}

// Note: This is currently never `provide`d anywhere, it's used by Storybook to swap to card view

export default /*#__PURE__*/_defineComponent({
  __name: 'PageStoreProfile',
  setup(__props) {

const displayType = inject<DisplayType>('displayType', 'table')

//translations
const { t } = useI18n({ useScope: 'global' })
const formLabel = (key: string) => t(`form.${key}`)
const storeItemsLabel = (key: string) => t(`storeItems.${key}`)

//util
const isNative = computed(() => Capacitor.isNativePlatform())

//get store data
const { getStores } = storeToRefs(useStoreProfiles())
const { getStore } = useStoreProfiles()
const storeID = ref(useRoute().params.id as string)
const storeData = ref(getStores.value.data)
const store = computed(() => {
  return getStore(storeID.value as string)
})
onBeforeUpdate(async () => {
  date.value = inject('date', ref(new Date())).value
  storeID.value = useRoute().params.id as string
  storeData.value = getStores.value.data
})
const activeData = computed(() => {
  if (getActiveParentTimer.value?.data.store_name === store.value?.store_name) {
    const { getCurrentParentTime } = storeToRefs(useTimers())
    return { active: true, time: getCurrentParentTime }
  } else {
    return { active: false, time: undefined }
  }
})

//timers
const date = inject('date', ref(new Date()))

const { addParentTimer, stopActiveParentTimer } = useTimers()
const startVisit = checkMaintenance(() => {
  //prevent starting the task if the rep has any other activity running
  if (getActiveParentTimer.value) {
    modalStoreEndVisitWarning.value = true
    return void 0
  }
  if (!isLocked.value) {
    const timestamp = new Date()
    timestamp.setFullYear(date.value.getFullYear())
    timestamp.setMonth(date.value.getMonth())
    timestamp.setDate(date.value.getDate())
    timestamp.setSeconds(0)
    timestamp.setMilliseconds(0)
    void addParentTimer(
      {
        type: 'store',
        data: {
          store_name: store.value!.store_name,
          store_id: storeID.value,
          store_number: store.value!.store_number,
        },
      },
      timestamp
    )
  }
})
const modalStoreEndVisitWarning = ref(false)
const endVisit = checkMaintenance(() => {
  //if a rep is logging time against a task, they should not be able to end this task
  if (getActiveChildTimer.value) {
    modalStoreEndVisitWarning.value = true
    return void 0
  }
  const timestamp = new Date()
  timestamp.setFullYear(date.value.getFullYear())
  timestamp.setMonth(date.value.getMonth())
  timestamp.setDate(date.value.getDate())
  timestamp.setSeconds(0)
  timestamp.setMilliseconds(0)
  stopActiveParentTimer({ date: timestamp })
  showResume.value = true
  setTimeout(() => {
    showResume.value = false
  }, 120000)
})
const { getActiveParentTimer, getActiveChildTimer, getAllParentTimers, isLocked } = storeToRefs(
  useTimers()
)
const isThisTimer = computed(() => {
  if (getActiveParentTimer.value && getActiveParentTimer.value.data.store_id) {
    return getActiveParentTimer.value?.data.store_id === storeID.value
  } else return false
})
const noActiveTimer = computed((): boolean => {
  return !getActiveParentTimer.value ? true : false
})
const thisStoreInTimers = computed((): boolean => {
  return getAllParentTimers.value.some((timer) => {
    return timer.type === 'store' && timer.data.store_id == storeID.value
  })
})

const showResume = ref(false)

const storeStates = reactive<StoreStates>({
  isThisTimer,
  noActiveTimer,
  thisStoreInTimers,
  showResume,
})

//tasks
const { getTaskData } = useStoreProfiles()
const taskData = computed(() => {
  return getTaskData(storeID.value as string)
})

const taskCount = computed(() => {
  if (!taskData.value) return 0
  else {
    return taskData.value.data.filter((task) => {
      return task.task_status_id != 9 && task.task_status_id != 22
    }).length
  }
})

//tabs
const tab = ref('tasks')
watch(storeID, () => {
  if (storeID.value) tab.value = 'tasks'
})
//settings for tasks
const isFetching = ref(false)

const user = storeToRefs(useUserStore())
const renderRecommendations = (data: string) => {
  return data.replaceAll('*', '<br />')
}
const storeRecommendations = computed(() => {
  if (store.value && user.language.value === 'fr_CA' && store.value.fr_recommendations) {
    return renderRecommendations(store.value.fr_recommendations as string)
  }
  if (store.value && store.value.en_recommendations) {
    return renderRecommendations(store.value.en_recommendations as string)
  }
  return ''
})

//Product line
const { getPartsByStoreID } = useInventory()
const productLinesData = computed(() => {
  //@ts-expect-error will not be in table
  return getPartsByStoreID(storeID.value)
})

return (_ctx: any,_cache: any) => {
  const _component_q_avatar = _resolveComponent("q-avatar")!
  const _component_q_badge = _resolveComponent("q-badge")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_tab = _resolveComponent("q-tab")!
  const _component_q_tabs = _resolveComponent("q-tabs")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_tab_panel = _resolveComponent("q-tab-panel")!
  const _component_q_tab_panels = _resolveComponent("q-tab-panels")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("h1", _hoisted_4, _toDisplayString(store.value?.store_name && store.value.store_number
              ? `${store.value.store_name} ${store.value.store_number}`
              : 'Store not found'), 1),
          (
            _unref(parseBool)(store.value?.remote_flag) ||
            _unref(parseBool)(store.value?.network_flag) ||
            (store.value && store.value.french_region_ind > 0)
          )
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                (_unref(parseBool)(store.value?.network_flag))
                  ? (_openBlock(), _createBlock(_component_q_avatar, {
                      key: 0,
                      size: "md",
                      rounded: "",
                      color: "grey-4",
                      icon: "hub"
                    }))
                  : _createCommentVNode("", true),
                (_unref(parseBool)(store.value?.remote_flag))
                  ? (_openBlock(), _createBlock(_component_q_avatar, {
                      key: 1,
                      size: "md",
                      rounded: "",
                      color: "grey-4",
                      icon: "hiking"
                    }))
                  : _createCommentVNode("", true),
                (store.value && store.value.french_region_ind > 0)
                  ? (_openBlock(), _createBlock(_component_q_avatar, {
                      key: 2,
                      size: "md",
                      rounded: "",
                      color: "grey-4"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" FR ")
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_6, [
          (activeData.value.active)
            ? (_openBlock(), _createBlock(_component_q_badge, {
                key: 0,
                color: "running",
                class: "q-py-xs text-body2"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(storeItemsLabel('visiting')) + " " + _toDisplayString(_unref(useFormatTime)(activeData.value.time!)), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _createVNode(ModalStoreEndVisitWarning, {
        modelValue: modalStoreEndVisitWarning.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modalStoreEndVisitWarning).value = $event))
      }, null, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_7, [
        (storeStates.isThisTimer)
          ? (_openBlock(), _createBlock(_component_q_btn, {
              key: 0,
              "no-caps": "",
              rounded: "",
              onClick: _unref(endVisit),
              color: "primary",
              label: formLabel('endStore')
            }, null, 8, ["onClick", "label"]))
          : (
          storeStates.noActiveTimer && storeStates.thisStoreInTimers && storeStates.showResume
        )
            ? (_openBlock(), _createBlock(_component_q_btn, {
                key: 1,
                "no-caps": "",
                rounded: "",
                onClick: _unref(startVisit),
                color: "primary",
                label: formLabel('resumeStore')
              }, null, 8, ["onClick", "label"]))
            : (_openBlock(), _createBlock(_component_q_btn, {
                key: 2,
                "no-caps": "",
                rounded: "",
                onClick: _unref(startVisit),
                color: "primary",
                label: formLabel('startStore')
              }, null, 8, ["onClick", "label"]))
      ])
    ]),
    _createVNode(_component_q_tabs, {
      modelValue: tab.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((tab).value = $event)),
      class: "text-grey",
      "active-color": "secondary",
      "indicator-color": "secondary",
      align: "justify",
      "narrow-indicator": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_tab, {
          name: "details",
          label: storeItemsLabel('details')
        }, null, 8, ["label"]),
        _createVNode(_component_q_tab, {
          name: "tasks",
          label: storeItemsLabel('tasks')
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_badge, {
              color: "secondary",
              floating: ""
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(taskCount.value), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["label"]),
        _createVNode(_component_q_tab, {
          name: "product",
          label: storeItemsLabel('productLines')
        }, null, 8, ["label"]),
        _createVNode(_component_q_tab, {
          name: "picklist",
          label: storeItemsLabel('picklist')
        }, null, 8, ["label"]),
        _createVNode(_component_q_tab, {
          name: "recommendations",
          label: storeItemsLabel('recommendations')
        }, null, 8, ["label"]),
        _createVNode(_component_q_tab, {
          name: "survey",
          label: storeItemsLabel('survey')
        }, null, 8, ["label"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_q_separator),
    _createVNode(_component_q_tab_panels, {
      "keep-alive": "",
      modelValue: tab.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((tab).value = $event)),
      animated: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_tab_panel, { name: "details" }, {
          default: _withCtx(() => [
            (store.value)
              ? (_openBlock(), _createBlock(StoreBase, {
                  key: 0,
                  store: store.value,
                  states: storeStates,
                  "active-data": activeData.value
                }, null, 8, ["store", "states", "active-data"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_component_q_tab_panel, {
          "keep-alive": "",
          name: "tasks"
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(StoreTasks, {
              key: `${storeID.value}`,
              "store-id": storeID.value,
              "is-fetching": isFetching.value,
              "is-native": isNative.value,
              "store-states": storeStates
            }, null, 8, ["store-id", "is-fetching", "is-native", "store-states"]))
          ]),
          _: 1
        }),
        _createVNode(_component_q_tab_panel, {
          "keep-alive": "",
          name: "product"
        }, {
          default: _withCtx(() => [
            _createVNode(FilteringDataDisplay, {
              "table-data": productLinesData.value,
              "enable-filters": true,
              "display-type": _unref(displayType)
            }, null, 8, ["table-data", "display-type"])
          ]),
          _: 1
        }),
        _createVNode(_component_q_tab_panel, { name: "recommendations" }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_8, _toDisplayString(storeItemsLabel('recommendations')), 1),
            _createElementVNode("p", { innerHTML: storeRecommendations.value }, null, 8, _hoisted_9)
          ]),
          _: 1
        }),
        _createVNode(_component_q_tab_panel, {
          "keep-alive": "",
          name: "picklist"
        }, {
          default: _withCtx(() => [
            _createVNode(StorePicklist, {
              key: "picklist",
              "store-id": storeID.value,
              "is-fetching": isFetching.value
            }, null, 8, ["store-id", "is-fetching"])
          ]),
          _: 1
        }),
        _createVNode(_component_q_tab_panel, { name: "survey" }, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(StoreRecaps, {
              key: `${storeID.value}-store-recaps`,
              "store-id": storeID.value
            }, null, 8, ["store-id"]))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})